import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Alert, CircularProgress, Card, CardContent,
  Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  ToggleButton, ToggleButtonGroup, Paper, Grid,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { getStockRecommendations, getCnStockRecommendations } from '../utils/api';
import { useI18n } from '../utils/i18n';
import { isLoggedIn } from '../utils/auth';
import GuestWall from '../components/GuestWall';

const GUEST_PREVIEW_ROWS = 3;

const signalColor = (signal) => {
  const s = (signal || '').toLowerCase();
  if (s === 'strong_buy' || s === 'buy') return 'success';
  if (s === 'sell') return 'error';
  return 'default';
};

export default function RecommendationsPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [market, setMarket] = useState('us');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [usData, setUsData] = useState(null);
  const [cnData, setCnData] = useState(null);

  const handleGenerate = async () => {
    setError('');
    setLoading(true);
    try {
      if (market === 'us') {
        const data = await getStockRecommendations(null, 20, 5);
        setUsData(data);
      } else {
        const data = await getCnStockRecommendations(null, 20, 5);
        setCnData(data);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 0.5 }}>{t('rec.title')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('rec.subtitle')}
      </Typography>

      <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <ToggleButtonGroup value={market} exclusive onChange={(_, v) => v && setMarket(v)} size="small">
          <ToggleButton value="us">{t('rec.usMarket')}</ToggleButton>
          <ToggleButton value="cn">{t('rec.cnMarket')}</ToggleButton>
        </ToggleButtonGroup>
        <Button variant="contained" startIcon={<AutoAwesomeIcon />} onClick={handleGenerate} disabled={loading}>
          {loading ? t('rec.generating') : t('rec.generate')}
        </Button>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>}

      {market === 'us' && usData && !loading && (
        <TrendRecommendations
          data={usData}
          currency="$"
          onTickerClick={(tk) => navigate(`/stock?ticker=${tk}`)}
          isGuest={!isLoggedIn()}
        />
      )}

      {market === 'cn' && cnData && !loading && (
        <TrendRecommendations
          data={cnData}
          currency="¥"
          onTickerClick={null}
          isGuest={!isLoggedIn()}
        />
      )}
    </Box>
  );
}

/**
 * Unified renderer for the trend-scan response (run_trend_scan / run_cn_trend_scan):
 * { market_regime?, top_picks: [...], sector_picks: {sector: [...]}, total_scored, buy_signals }
 */
function TrendRecommendations({ data, currency, onTickerClick, isGuest }) {
  const { t } = useI18n();
  const regime = data?.market_regime || null;
  const topPicks = data?.top_picks || [];
  const sectorPicks = data?.sector_picks || {};

  // Sectors with at least one pick, sorted by their top score
  const sortedSectors = Object.entries(sectorPicks)
    .filter(([, recs]) => Array.isArray(recs) && recs.length > 0)
    .sort((a, b) => (b[1][0]?.score || 0) - (a[1][0]?.score || 0));

  const clickable = typeof onTickerClick === 'function';

  const renderRow = (r) => {
    const sig = (r.signal || '').replace('_', ' ').toUpperCase();
    return (
      <TableRow
        key={r.ticker}
        hover={clickable}
        sx={clickable ? { cursor: 'pointer' } : undefined}
        onClick={clickable ? () => onTickerClick(r.ticker) : undefined}
      >
        <TableCell>
          <Typography fontWeight={600}>{r.ticker}</Typography>
          {r.name && <Typography variant="caption" color="text.secondary">{r.name}</Typography>}
        </TableCell>
        <TableCell><Chip label={sig || 'N/A'} size="small" color={signalColor(r.signal)} /></TableCell>
        <TableCell align="right">{(r.score || 0).toFixed(1)}</TableCell>
        <TableCell align="right">{currency}{(r.price || 0).toFixed(2)}</TableCell>
        <TableCell align="right">
          <Typography color={(r.ret_1m || 0) >= 0 ? 'success.main' : 'error.main'}>
            {(r.ret_1m || 0) >= 0 ? '+' : ''}{(r.ret_1m || 0).toFixed(1)}%
          </Typography>
        </TableCell>
        <TableCell align="right">{(r.rsi || 0).toFixed(0)}</TableCell>
        <TableCell><Typography variant="caption">{r.sector || ''}</Typography></TableCell>
      </TableRow>
    );
  };

  const headerRow = (
    <TableRow>
      <TableCell>{t('rec.ticker')}</TableCell>
      <TableCell>{t('rec.signal')}</TableCell>
      <TableCell align="right">{t('rec.score')}</TableCell>
      <TableCell align="right">{t('rec.price')}</TableCell>
      <TableCell align="right">{t('rec.ret1m')}</TableCell>
      <TableCell align="right">{t('rec.rsi')}</TableCell>
      <TableCell>{t('rec.sector')}</TableCell>
    </TableRow>
  );

  return (
    <Box>
      {/* Market Health / Regime Highlight */}
      <Card sx={{ mb: 3, border: 1, borderColor: 'divider', background: 'linear-gradient(135deg, rgba(25,118,210,0.06) 0%, rgba(25,118,210,0.02) 100%)' }}>
        <CardContent sx={{ py: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700}>{t('rec.marketHealth')}</Typography>
            {regime?.regime && (
              <Chip
                label={regime.regime}
                size="small"
                color={regime.regime === 'OVERBOUGHT' ? 'warning' : regime.regime === 'OVERSOLD' ? 'error' : 'success'}
              />
            )}
          </Box>
          <Grid container spacing={3}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="caption" color="text.secondary">{t('rec.stocksScored')}</Typography>
              <Typography variant="h5" fontWeight={700}>{data?.total_scored || 0}</Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="caption" color="text.secondary">{t('rec.buySignals')}</Typography>
              <Typography variant="h5" fontWeight={700} color="success.main">{data?.buy_signals || 0}</Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="caption" color="text.secondary">{t('rec.sectorsWithPicks')}</Typography>
              <Typography variant="h5" fontWeight={700}>{sortedSectors.length}</Typography>
            </Grid>
            {regime?.spy_rsi != null && (
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">{t('rec.benchmarkRsi')}</Typography>
                <Typography variant="h5" fontWeight={700}>{Number(regime.spy_rsi).toFixed(0)}</Typography>
              </Grid>
            )}
          </Grid>
          {sortedSectors.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>{t('rec.topSectors')}</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {sortedSectors.slice(0, 5).map(([name, recs]) => (
                  <Chip
                    key={name}
                    label={`${name}: ${(recs[0]?.score || 0).toFixed(1)}`}
                    size="small"
                    variant="outlined"
                    color="success"
                  />
                ))}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Overall Top Picks */}
      {topPicks.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2">{t('rec.topPicks')}</Typography>
              <Chip label={`${topPicks.length} picks`} size="small" color="success" />
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>{headerRow}</TableHead>
                <TableBody>
                  {topPicks.slice(0, isGuest ? GUEST_PREVIEW_ROWS : 20).map(renderRow)}
                </TableBody>
              </Table>
            </TableContainer>
            {isGuest && topPicks.length > GUEST_PREVIEW_ROWS && (
              <GuestWall visible height={180}>
                <Table size="small">
                  <TableBody>
                    {topPicks.slice(GUEST_PREVIEW_ROWS, GUEST_PREVIEW_ROWS + 5).map(renderRow)}
                  </TableBody>
                </Table>
              </GuestWall>
            )}
          </CardContent>
        </Card>
      )}

      {/* Per-Sector Picks */}
      {sortedSectors.length > 0 && (
        <GuestWall visible={isGuest} height={300}>
          {sortedSectors.map(([sectorName, recs]) => (
            <Card key={sectorName} sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle2">{sectorName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('rec.top')}: {recs[0]?.ticker} ({(recs[0]?.score || 0).toFixed(1)})
                    </Typography>
                  </Box>
                  <Chip label={`${recs.length} picks`} size="small" color="success" />
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>{headerRow}</TableHead>
                    <TableBody>{recs.map(renderRow)}</TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          ))}
        </GuestWall>
      )}
    </Box>
  );
}
